import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import * as React from "react";
import Icon from "../../../components/atoms/Icon";
import Loader from "../../../components/atoms/Loader";
import {
  HorizontalScrollTable,
  TableColumn,
} from "../../../components/atoms/Table";
import { SelectWrapper } from "../../../components/general/SelectWrapper";
import { Text } from "../../../components/general/Text";
import TabsNavi from "../../../components/molecules/TabsNavi";
import {
  countAwaitingMyApproval,
  LEARNING_COACH_APPLICATION_REQUEST_STATUSES,
  type LearningCoachApplicationRequest,
  type LearningCoachApplicationRequestStatus,
  toApplicationRequestStateIcon,
  toApplicationRequestStateLabel,
  toStatusLabel,
} from "../../../domains/LearningCoachApplicationRequestStatus";
import { canIssueLearningCoachApplication } from "../../../helpers/LearningCoachApplicationHelper";
import { useQueryError } from "../../../hooks/http/useQueryError";
import { OperatorPrivilege } from "../../../interfaces/OperatorInterface";
import SectionInterface from "../../../interfaces/SectionInterface";
import { ApplicationRequestIssuance } from "../ApplicationRequestIssuance";
import { useFetchLearningCoachApplicationRequests } from "./useFetchLearningCoachApplicationRequests";

interface Props {
  section: SectionInterface;
  privileges: OperatorPrivilege[];
}

type StatusFilter = LearningCoachApplicationRequestStatus | "all";

const STATUS_FILTER_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "すべて" },
  ...LEARNING_COACH_APPLICATION_REQUEST_STATUSES.map((status) => ({
    value: status,
    label: toStatusLabel(status),
  })),
];

const TABLE_HEADERS = [
  { text: "生徒名（学年・生徒ID）", centering: false },
  { text: "コース", centering: false },
  { text: "URL発行日時", centering: false },
  { text: "状態", centering: false },
];

// 起票時点の氏名に、登録されている学年と生徒 ID だけを併記する（未登録なら氏名のみ）
const toStudentLabel = ({
  student,
}: LearningCoachApplicationRequest): string => {
  const name = `${student.lastName} ${student.firstName}`;
  const details = [student.jobHuman, student.code].filter(Boolean);
  return details.length > 0 ? `${name}（${details.join("・")}）` : name;
};

const ApplicationRequestState = ({
  request,
}: {
  request: LearningCoachApplicationRequest;
}) => {
  const icon = toApplicationRequestStateIcon(request);
  return (
    <span className="inline-flex items-center gap-2">
      {icon && <Icon name={icon} color="black" />}
      {toApplicationRequestStateLabel(request)}
    </span>
  );
};

// 自分が起票した先生メール承認待ちを一覧の上で知らせる（LIST-04）
const AwaitingMyApprovalAlert = ({ count }: { count: number }) => {
  if (count === 0) {
    return null;
  }
  return (
    <div
      role="alert"
      className="mt-7 flex gap-4 rounded border border-solid border-yellow-400 bg-[#fff6e5] px-6 py-4"
    >
      <Icon name="icon-warning" className="shrink-0 fill-[#cc730d]" />
      <div>
        <p className="m-0">先生メール承認待ちの申込が{count}件あります。</p>
        <p className="m-0 text-sm">
          承認依頼メールから承認・却下してください。対応がない場合は自動で承認されます。
        </p>
      </div>
    </div>
  );
};

const ApplicationRequestsTable = ({
  applicationRequests,
}: {
  applicationRequests: LearningCoachApplicationRequest[];
}) => (
  <div className="mt-4">
    <HorizontalScrollTable
      headers={TABLE_HEADERS}
      rows={applicationRequests.map((request) => (
        <tr key={request.id}>
          {/* 生徒一覧の氏名の列と同じく、最小幅を持たせて空白のところだけで改行する。ほかの列は折り返さず、表を横にスクロールさせる */}
          <TableColumn className="min-w-[11.6rem] whitespace-normal break-keep">
            {toStudentLabel(request)}
          </TableColumn>
          <TableColumn noWrap>{request.courseName}</TableColumn>
          <TableColumn noWrap>
            {format(parseISO(request.urlIssuedAt), "yyyy/MM/dd HH:mm")}
          </TableColumn>
          <TableColumn noWrap>
            <ApplicationRequestState request={request} />
          </TableColumn>
        </tr>
      ))}
    />
  </div>
);

const SectionsLearningCoachApplicationRequests = ({
  section,
  privileges,
}: Props) => {
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");
  const {
    data: applicationRequests,
    isPending,
    error,
  } = useFetchLearningCoachApplicationRequests({ sectionId: section.id });

  useQueryError(error);

  // 並び順は boron が返した順のまま。絞り込みは状態だけ（LIST-01）
  const filteredRequests =
    statusFilter === "all"
      ? applicationRequests
      : applicationRequests?.filter(({ status }) => status === statusFilter);

  const renderBody = () => {
    if (isPending) {
      return <Loader loading />;
    }
    // エラーは共通処理に任せ、一覧や正常な0件の本文は表示しない
    if (error || !applicationRequests || !filteredRequests) {
      return null;
    }
    if (filteredRequests.length === 0) {
      // 絞り込みの結果が 0 件の場合は、申込依頼が 1 件もない場合と文言を区別する（LIST-03。文言は暫定）
      return (
        <Text align="center" className="mt-8">
          {applicationRequests.length > 0
            ? "絞り込み条件に一致する申込依頼はありません。"
            : "申込依頼はまだありません。"}
        </Text>
      );
    }
    return <ApplicationRequestsTable applicationRequests={filteredRequests} />;
  };

  return (
    <div>
      <TabsNavi tabs={[{ label: "申込一覧", link: "#", active: true }]} />
      <AwaitingMyApprovalAlert
        count={countAwaitingMyApproval(applicationRequests ?? [])}
      />
      <div className="mt-7 flex items-center justify-between gap-4">
        {/* 「ステータス：保護者申込待ち」まで切れずに入る幅にする */}
        <div className="w-[26rem]">
          <SelectWrapper
            aria-label="ステータス"
            options={STATUS_FILTER_OPTIONS}
            value={STATUS_FILTER_OPTIONS.find(
              ({ value }) => value === statusFilter,
            )}
            onChange={(option) => {
              if (option) {
                setStatusFilter(option.value as StatusFilter);
              }
            }}
            formatOptionLabel={(option, { context }) =>
              context === "value"
                ? `ステータス：${option.label}`
                : String(option.label)
            }
            isSearchable={false}
          />
        </div>
        {/* 起票できる役割にだけ入口を出す（ACC-03） */}
        {canIssueLearningCoachApplication(privileges) && (
          <ApplicationRequestIssuance sectionId={section.id} />
        )}
      </div>
      {renderBody()}
    </div>
  );
};

export default SectionsLearningCoachApplicationRequests;
